import { Suspense, lazy } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Drawer, IconButton } from '@material-ui/core';

import { RiCloseLine } from '@remixicon/react';

import { useRouteRef } from '@backstage/core-plugin-api';

import { extractLastNameComponent } from '@spotify/confidence-core-react';

import { recordingRouteRef } from '../routes';
import type { RecordingHit } from './types';

const LazyRecordingPreviewSidebar = lazy(() =>
  import('@spotify/confidence-recordings-react').then(m => ({
    default: m.RecordingPreviewSidebar,
  })),
);

const ICON_SIZE = 16;

type RecordingDrawerProps = {
  recordings: RecordingHit[];
  activeIndex: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
};

export const RecordingDrawer = ({
  recordings,
  activeIndex,
  onClose,
  onNavigate,
}: RecordingDrawerProps) => {
  const recordingRoute = useRouteRef(recordingRouteRef);
  const isOpen = activeIndex !== null;
  const recording = activeIndex !== null ? recordings[activeIndex] : null;
  const recordingId = extractLastNameComponent(recording?.name) ?? '';
  const hasPrevious = activeIndex !== null && activeIndex > 0;
  const hasNext = activeIndex !== null && activeIndex < recordings.length - 1;

  const handlePrevious = hasPrevious
    ? () => onNavigate(activeIndex! - 1)
    : undefined;
  const handleNext = hasNext ? () => onNavigate(activeIndex! + 1) : undefined;

  useHotkeys(
    'shift+up',
    () => handlePrevious?.(),
    { enabled: !!handlePrevious, preventDefault: true },
    [handlePrevious],
  );
  useHotkeys(
    'shift+down',
    () => handleNext?.(),
    { enabled: !!handleNext, preventDefault: true },
    [handleNext],
  );

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={isOpen}
      onClose={onClose}
      SlideProps={{ unmountOnExit: true }}
      ModalProps={{ keepMounted: false }}
      PaperProps={{ style: { width: 680 } }}
    >
      {recording && (
        <Suspense fallback={null}>
          <LazyRecordingPreviewSidebar
            recordingName={recording.name}
            onPrevious={handlePrevious}
            onNext={handleNext}
            viewRecordingHref={recordingRoute({ id: recordingId })}
            actionsEnd={
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close recording preview"
              >
                <RiCloseLine size={ICON_SIZE} />
              </IconButton>
            }
          />
        </Suspense>
      )}
    </Drawer>
  );
};
